import { useNavigate } from "react-router-dom";

export default function GetStarted() {

    const navigate = useNavigate();

    const apiCategories = [
        {
            "title": "Real Time Request To Pay",
            "description": "This API allows merchants to initiate single or recurring payment requests in real-time from their website or app.",
            "links": [
                { "name": "/OnlinePayments" },
            ]
        },
        {
            "title": "Submit API",
            "description": "This API enables merchants to submit transaction details and check their transaction status.",
            "links": [
                { "name": "/GeneratedPaymentRequest" },
            ]
        },
        {
            "title": "Cancel Payment",
            "description": "This API allows merchants to cancel a payment request before or after initiation, ensuring control over payment workflows.",
            "links": [
                { "name": "/CancelaTransaction" }
            ]
        },
        {
            "title": "Transaction Search",
            "description": "The Transaction Search API provides the ability to search and retrieve transaction details based on specific parameters.",
            "links": [
                { "name": "/TransactionSearch" }
            ]
        },
        {
            "title": "Transaction Status",
            "description": "This API allows merchants to check the current status of a transaction, ensuring real-time updates on payment progress.",
            "links": [
                { "name": "/GetBulkTransactionStatus" }
            ]
        }
    ];

    const handleScrollTo = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" }); // Smooth scrolling effect
        }
    };

    return (
        <>
            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-8">

                    <div className="col-span-8 md:col-span-8" id="Introduction">
                        <p className="font-bold text-[32px]">BenePay API Reference</p>
                    </div>

                    <div className="col-span-8 md:col-span-8">
                        <hr className="border-t border-gray-300 my-2 w-full" />
                    </div>

                    <div className="col-span-8 md:col-span-8">
                        <p className="text-gray-700">
                            Welcome to the BeneCollect API Reference documentation. BeneCollect is a flagship software product from BenePay, designed to streamline payment processes for businesses. It facilitates efficient payment requests and collections from payers, allowing businesses to manage payment workflows seamlessly.
                        </p>
                        <p className="text-gray-700 mt-3">
                            BeneCollect leverages tokenized "Request to Pay" messaging to initiate payment requests securely, and supports a wide variety of payment methods including debit/credit cards, local bank accounts globally, UPI, Wallets, and QR codes. This provides businesses with unparalleled flexibility and convenience when collecting payments from customers worldwide.
                        </p>
                    </div>

                    <div className="mt-[30px] col-span-8 md:col-span-8 mb-3" id="APIReference">
                        <p className="font-bold text-[25px]">How to Use API Reference</p>
                    </div>

                    <div className="col-span-8 md:col-span-8">
                        <p className="text-gray-700 mb-4">
                            To get started with our API, we highly recommend you try using the <strong>Test Environment</strong> or <strong>Sandbox</strong>. You can experiment with most of the API calls in the API Reference by entering the required parameter values in the form and clicking <strong>Try It</strong> to see the response based on your inputs.
                        </p>
                        <p className="text-gray-700 mb-4">
                            The API Reference provides detailed specifications for each endpoint, including the necessary parameters, request bodies, expected responses, and error handling. It’s a comprehensive guide to successfully integrating the API with your system.
                        </p>
                    </div>

                    <div className="space-y-3 col-span-8 md:col-span-8">
                        <div className="p-4 bg-gray-50 border-l-4 border-blue-500 rounded">
                            <h3 className="font-semibold text-gray-800">Review API Security</h3>
                            <p className="text-gray-600">
                                Before diving into the API Reference, it's essential to first integrate the 
                                <span onClick={() => navigate("/encryption")} className="ml-1 text-blue-600 underline hover:text-blue-500 cursor-pointer">
                                    <strong>API Security</strong>
                                </span>. 
                                This guide outlines the key steps required to integrate the BeneCollect API into your environment, specifically for <strong>Submit API</strong> and <strong>Real Time Request To Pay</strong>, ensuring that you can quickly begin using the platform.
                            </p>
                        </div>
                        <div className="p-4 bg-gray-50 border-l-4 border-green-500 rounded">
                            <h3 className="font-semibold text-gray-800">Use Mock API Calls</h3>
                            <p className="text-gray-600">
                                The API Reference includes an interactive feature that allows you to make test API calls using predefined mock data. These mock calls help you verify your integration before using live data and provide a great way to understand how the API responds in various scenarios.
                            </p>
                        </div>
                        <div className="p-4 bg-gray-50 border-l-4 border-yellow-500 rounded">
                            <h3 className="font-semibold text-gray-800">Language Support</h3>
                            <p className="text-gray-600">
                                The API Reference provides code snippets and examples in a wide range of programming languages, including Python, C#, PHP, and Java. These examples are ready to be used and tailored to suit your application's specific needs, allowing for easy integration regardless of your preferred language.
                            </p>
                        </div>
                    </div>

                    <div className="mt-[30px] col-span-8 md:col-span-8 mb-3" id="HighLevelOverview">
                        <p className="font-bold text-[25px]">High Level Overview</p>
                    </div>

                    <div className="col-span-8 md:col-span-8">
                        <p className="text-gray-700 mb-4">
                            Below diagram indicates the high-level flow for payment processing via BenePay APIs.
                            Here a payer tries to make a payment against a product/service and the merchant website in turn calls BenePay (payment service provider for the merchant).
                        </p>
                        <img
                            src="/images/api-process.jpg"
                            alt="API Process Flow"
                            width={1200} // Adjust as needed
                            height={400} // Adjust as needed
                            layout="responsive"
                        />
                    </div>

                    <div className="mt-[30px] col-span-8 md:col-span-8 mb-3" id="ListofBeneCollectAPIs">
                        <p className="font-bold text-[25px]">List of BeneCollect APIs</p>
                    </div>

                    <div className="mt-1 col-span-8 md:col-span-8">
                        <h2 className="text-lg font-semibold mb-4">
                            The API Reference is categorized into the following key sections:
                        </h2>
                        <table className="min-w-full border border-gray-300">
                            <thead>
                                <tr className="bg-gray-100 text-gray-700">
                                    <th className="border border-gray-300 p-2">API's</th>
                                    <th className="border border-gray-300 p-2">Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {apiCategories.map((category, index) => (
                                    <tr key={index} className="bg-white">
                                        <td className="border border-gray-300 p-2 font-semibold text-nowrap">
                                            {category.title}<ul>
                                                {category.links.map((link, linkIndex) => (
                                                    <li key={linkIndex}>
                                                        <button 
                                                            onClick = {() => navigate(link.name.replace(/\s+/g, ''))}
                                                            type="button" 
                                                            // text-white bg-gray-500 hover:bg-blue-800 dark:hover:bg-blue-700  
                                                            className="mt-2 text-blue-700 font-medium rounded-lg text-sm px-1 text-center inline-flex items-center"
                                                        >
                                                            Go to API

                                                            <svg className="rtl:rotate-180 w-3.5 h-3.5 ms-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                                                                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 5h12m0 0L9 1m4 4L9 9"/>
                                                            </svg>
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </td>
                                        <td className="border border-gray-300 p-2">
                                            {category.description}
                                            
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-8 col-span-8 md:col-span-8 mb-3" id="GetSupport">
                        <p className="font-bold text-[25px]">Get Support</p>
                    </div>
                    <div className="mt-1 col-span-8 md:col-span-8">
                        <div className="flex-1">
                            <p className="text-gray-600">
                            If you encounter any issues or have questions during the integration process, our dedicated support team is ready to assist you. Please don’t hesitate to reach out, and we’ll ensure you have the resources you need to succeed.
                            </p>
                        </div>
                        <button className="mt-2 bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600">
                            Contact Support
                        </button>
                    </div>
                </div>

                <div className="hidden md:col-span-4 md:block">
                    <div className="sticky top-4 p-4">
                        <p className="text-gray-700 font-bold pl-5">TABLE OF CONTENT</p>
                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("Introduction")}>Introduction</button>
                        </div>

                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("APIReference")}>How to Use API Reference</button>
                        </div>

                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("ListofBeneCollectAPIs")}>List of BeneCollect APIs</button>
                        </div>

                        <div>
                            <button className="text-gray-700 pl-9 py-1" onClick={() => handleScrollTo("GetSupport")}>Get Support</button>
                        </div>
                    </div>
                </div>
            </div>

        </>
    );
}